import React from 'react';

import ViewportMgr from 'coreClasses/view/ViewportMgr';

import {ISpacingFields, IAlignmentFields, ISkeletonFields, TBreakpointsList} from './IGridContentful';

const breakpointsList = ViewportMgr.getBreakpoints();

/**
 * Fallback to the next smaller breakpoint's value
 * @param {string} currentBreakpoint
 * @param {string} property
 * @param {object} data
 * @returns {string}
 */
const getStylesFallback = <TData, TProperty>(
	currentBreakpoint: TBreakpointsList,
	property: TProperty,
	data: TData,
): string => {
	const currentBreakpointIndex = breakpointsList.indexOf(currentBreakpoint);

	// Recursive condition to go out of the loop
	if (currentBreakpointIndex === 0) {
		return undefined;
	}

	const prevBreakpoint = breakpointsList[currentBreakpointIndex - 1];
	const valuesObj = data[prevBreakpoint];

	// If the breakpoint is added later it won't exists in the data
	if (typeof valuesObj === 'undefined') {
		return undefined;
	}

	const value = valuesObj[property];

	if (value && value !== 'inherit') {
		return value;
	}

	return getStylesFallback(prevBreakpoint, property, data);
};

/**
 * Tarnsform the data from Contentful to valid CSS properties for the current breakpoint
 * @param {object} data
 * @param {object} propertiesMap
 * @returns {object}
 */
const transformStylesData = <TData, TCSSPropertiesMap>(
	data: TData,
	propertiesMap: TCSSPropertiesMap,
	breakPoint,
	inherit: boolean = true,
) => {
	const result: React.CSSProperties = {};
	const currentData = data && data[breakPoint];
	// use currentData instead of propertiesMap to get only the properties that are defined
	const properties = Object.keys(propertiesMap || {}) as Array<keyof typeof propertiesMap>;

	// Go out if the current breakpoint data doesn't exists
	if (typeof currentData === 'undefined' || currentData === null) {
		return result;
	}

	for (let i = 0, len = properties.length; i < len; i += 1) {
		const property = properties[i] as keyof React.CSSProperties;
		const value = currentData[property] as string;
		const hasValue = value && value !== 'inherit';

		if (hasValue) {
			result[propertiesMap[property]] = value;

			continue;
		}

		// Prevent fallback to the next smaller breakpoint
		if (!inherit) {
			continue;
		}

		// Fallback to the next smaller breakpoint
		const fallbackValue = getStylesFallback<TData, keyof React.CSSProperties>(breakPoint, property, data);

		if (!hasValue && fallbackValue) {
			result[propertiesMap[property]] = fallbackValue;

			continue;
		}
	}

	return result;
};

/**
 * Transform the spacing fields data from Contentful to valid CSS properties for the current breakpoint
 * @param {object} data - data
 * @param {String} breakPoint - breakpoint
 * @returns {object}
 */
const transformSpacingData = (data: ISpacingFields, breakPoint) => {
	// Exit if the data is not an object
	if (data === null || typeof data !== 'object') {
		return {};
	}

	// Map the keys to the CSS properties
	// Top and bottom are margins, left and right are paddings, same as bootstrap
	const propertiesMap = {
		top: 'margin-block-start',
		right: 'padding-inline-end',
		bottom: 'margin-block-end',
		left: 'padding-inline-start',
	};

	return transformStylesData<ISpacingFields, typeof propertiesMap>(data, propertiesMap, breakPoint, false);
};

/**
 * Transform the skeleton height fields data from Contentful to valid CSS properties for the current breakpoint
 * @param {object} data - data
 * @param {String} breakPoint - breakpoint
 * @returns {object}
 */
const transformSkeletonHeightData = (data: ISkeletonFields, breakPoint) => {
	// Exit if the data is not an object
	if (data === null || typeof data !== 'object') {
		return {};
	}

	// Map the keys to the CSS properties
	// Top and bottom are margins, left and right are paddings, same as bootstrap
	const propertiesMap = {
		height: 'height',
	};

	return transformStylesData<ISkeletonFields, typeof propertiesMap>(data, propertiesMap, breakPoint, false);
};

/**
 * Transform the alignment fields data from Contentful to valid CSS properties for the current breakpoint
 * @param {object} data - data
 * @param {String} breakPoint - breakpoint
 * @returns {object}
 */
const transformAlignmentData = (data: IAlignmentFields, breakPoint) => {
	// Exit if the data is not an object
	if (data === null || typeof data !== 'object') {
		return {};
	}

	const mappedValues = {
		start: 'flex-start',
		end: 'flex-end',
	};

	// Map the keys to the CSS properties
	const propertiesMap = {
		direction: 'flex-direction',
		cross: 'align-items',
		main: 'justify-content',
		text: 'text-align',
	};

	// This data is re-mapped to the correct CSS values
	const result = {};
	const transformedData = transformStylesData<IAlignmentFields, typeof propertiesMap>(
		data,
		propertiesMap,
		breakPoint,
	);
	const transformedDataKeys = Object.keys(transformedData || {});

	// Map to the correct CSS properties
	for (let i = 0, len = transformedDataKeys.length; i < len; i += 1) {
		const property = transformedDataKeys[i];
		const value = transformedData[property];
		const hasValue = value && value !== 'inherit';

		if (hasValue) {
			result[property] = mappedValues[value] || value;
		}
	}

	return result;
};

/**
 * Construct CSS classes based on the given attributes for all available breakPoints
 * @param id - uniqueID
 * @param spacingData - spacingData
 * @param alignmentData - alignmentData
 * @param skeletonData - skeletonData
 * @param responsiveData - responsiveData
 */
export const constructGridCssStyle = (
	id: string,
	spacingData: ISpacingFields,
	alignmentData: IAlignmentFields,
	skeletonData = null,
	responsiveData = null,
): {} => {
	const dynamicClasses = {};
	const breakPointsMap = ViewportMgr.getBreakpointsMap();
	const breakPointsMapKeys = Object.keys(breakPointsMap);
	const breakPointsMapKeysLength = breakPointsMapKeys.length;

	breakPointsMapKeys.forEach((b, i) => {
		const classKey = `c-${b}-${id}`;
		const spacing = transformSpacingData(spacingData, b);
		const alignment = transformAlignmentData(alignmentData, b);
		const height = skeletonData ? transformSkeletonHeightData(skeletonData, b) : {};
		const order = responsiveData && !isNaN(responsiveData[b]?.order) ? responsiveData[b]?.order.toString() : null;
		const display = responsiveData && responsiveData[b]?.display;
		const nb = breakPointsMapKeysLength >= i + 1 ? i + 1 : null;
		const currentBreakpointWidth = breakPointsMap[b];
		const nextBreakpointWidth = nb ? breakPointsMap[breakPointsMapKeys[nb]] : null;
		const mediaRules = [`(min-width: ${currentBreakpointWidth}px)`];

		if (nextBreakpointWidth) {
			mediaRules.push(`(max-width: ${nextBreakpointWidth}px)`);
		}

		const spacingKeys = Object.keys(spacing);
		const alignmentKeys = Object.keys(alignment);
		const heightKeys = Object.keys(height);

		if (spacingKeys.length > 0 || alignmentKeys.length > 0 || heightKeys.length > 0 || order) {
			dynamicClasses[classKey] = `@media ${mediaRules.join(' and ')} {.${classKey}{`;

			if (spacingKeys.length > 0) {
				dynamicClasses[classKey] += ` ${Object.keys(spacing).map((r) => `${r}: ${spacing[r]} !important;`)
					.join(' ')} `;
			}

			if (alignmentKeys.length > 0) {
				// eslint-disable-next-line max-len
				dynamicClasses[classKey] += ` ${Object.keys(alignment).map((r) => `${r}: ${alignment[r]};`)
					.join(' ')} `;
			}

			if (heightKeys.length > 0) {
				dynamicClasses[classKey] += ` ${Object.keys(height).map((r) => `${r}: ${height[r]} !important;`)
					.join(' ')} `;
			}

			if (order) {
				dynamicClasses[classKey] += ` order: ${order.toString()}; `;
			}

			if (display) {
				dynamicClasses[classKey] += ` display: ${display}; `;
			}

			dynamicClasses[classKey] += '}}';
		}
	});

	return dynamicClasses;
};
